import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Home, Volume2, VolumeX, Pause, Play } from 'lucide-react';
import LocalAudioCache from '../utils/localAudioCache';

const DomainStoryFuture = () => {
  const { domainId } = useParams();
  const navigate = useNavigate();
  
  // Check if data exists - skip loading if so
  const hasUnifiedData = typeof window !== 'undefined' && localStorage.getItem('unified_story_data');
  
  const [isLoading, setIsLoading] = useState(!hasUnifiedData);
  const [progress, setProgress] = useState(hasUnifiedData ? 100 : 0);
  const [isNarrating, setIsNarrating] = useState(false);
  const [showNarration, setShowNarration] = useState(false);
  const [selectedYear, setSelectedYear] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState('hinglish');
  const [audioReady, setAudioReady] = useState(hasUnifiedData);
  const [showSkipButton, setShowSkipButton] = useState(hasUnifiedData);
  const [storyCompleted, setStoryCompleted] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioLoaded, setAudioLoaded] = useState(false);
  
  const audioRef = useRef(null);
  const audioCache = useRef({ hindi: {}, english: {}, hinglish: {} });

  // Domain configurations - supports both numeric IDs ('1'-'8') and string keys
  const domainConfigs = {
    '1': { name: 'Health & Longevity', icon: '💚', color: '#10b981', theme: 'health' },
    '2': { name: 'Career & Success', icon: '💼', color: '#f59e0b', theme: 'career' },
    '3': { name: 'Love & Romance', icon: '💗', color: '#ec4899', theme: 'love' },
    '4': { name: 'Wealth & Money', icon: '💰', color: '#10b981', theme: 'wealth' },
    '5': { name: 'Family & Home', icon: '🏡', color: '#06b6d4', theme: 'family' },
    '6': { name: 'Learning & Skills', icon: '🎓', color: '#6366f1', theme: 'education' },
    '7': { name: 'Spirit & Growth', icon: '🌅', color: '#8b5cf6', theme: 'spirituality' },
    '8': { name: 'Travel & Adventure', icon: '✈️', color: '#14b8a6', theme: 'travel' },
    // String key aliases
    'health': { name: 'Health & Longevity', icon: '💚', color: '#10b981', theme: 'health' },
    'career': { name: 'Career & Success', icon: '💼', color: '#f59e0b', theme: 'career' },
    'love': { name: 'Love & Romance', icon: '💗', color: '#ec4899', theme: 'love' },
    'wealth': { name: 'Wealth & Money', icon: '💰', color: '#10b981', theme: 'wealth' },
    'family': { name: 'Family & Home', icon: '🏡', color: '#06b6d4', theme: 'family' },
    'education': { name: 'Learning & Skills', icon: '🎓', color: '#6366f1', theme: 'education' },
    'spirituality': { name: 'Spirit & Growth', icon: '🌅', color: '#8b5cf6', theme: 'spirituality' },
    'spiritual': { name: 'Spirit & Growth', icon: '🌅', color: '#8b5cf6', theme: 'spirituality' },
    'travel': { name: 'Travel & Adventure', icon: '✈️', color: '#14b8a6', theme: 'travel' },
  };

  // Safe domain lookup with fallback
  const domain = domainConfigs[domainId] || domainConfigs[domainId?.toLowerCase?.()] || domainConfigs['1'];

  // Future predictions for this domain (mock data - 10 years)
  // Get AI content for future reels
  const getAiContent = () => {
    try {
      const aiData = localStorage.getItem('ai_content_data');
      if (aiData) {
        const parsed = JSON.parse(aiData);
        return parsed.future_reels || {};
      }
    } catch (e) {
      console.log('[FutureStory] AI content not available');
    }
    return {};
  };
  
  const aiReels = getAiContent();
  const domainTheme = domain.theme;
  const aiDomainContent = aiReels[domainTheme] || aiReels[domainId] || '';

  // Get the REAL future reel text from unified_story_data
  const getRealDomainText = (lang) => {
    try {
      const data = JSON.parse(localStorage.getItem('unified_story_data') || '{}');
      const reels = data.future_story?.domain_reels || data.story_data?.future_story?.domain_reels || {};
      // Try both structures: reels[domain][lang] and reels[lang][domain]
      const text = reels[domainTheme]?.[lang] || reels[domainId]?.[lang] || 
                   reels[lang]?.[domainTheme] || reels[lang]?.[domainId] || '';
      return text;
    } catch { return ''; }
  };

  const realText = getRealDomainText(selectedLanguage);

  const futurePredictions = Array.from({ length: 10 }, (_, i) => {
    const year = 2025 + i;
    
    // Use REAL AI content from unified_story_data, or fallback to generic
    const baseContent = realText || aiDomainContent || `In ${year}, your ${domain.name.toLowerCase()} journey will bring significant growth.`;
    
    return {
      year: year.toString(),
      narration: {
        english: i === 0 && (realText || aiDomainContent) ? getRealDomainText('english') || baseContent : `In ${year}, your ${domain.name.toLowerCase()} journey will bring ${i % 2 === 0 ? 'significant growth' : 'new opportunities'}. The planetary alignments suggest ${i % 3 === 0 ? 'favorable conditions' : 'positive changes'} ahead.`,
        hindi: i === 0 && (realText || aiDomainContent) ? getRealDomainText('hindi') || baseContent : `${year} में, आपकी ${domain.name} यात्रा में ${i % 2 === 0 ? 'महत्वपूर्ण वृद्धि' : 'नए अवसर'} होंगे। ग्रह योग ${i % 3 === 0 ? 'अनुकूल परिस्थितियां' : 'सकारात्मक बदलाव'} लाएंगे।`,
        hinglish: i === 0 && (realText || aiDomainContent) ? getRealDomainText('hinglish') || baseContent : `${year} mein, aapki ${domain.name} journey mein ${i % 2 === 0 ? 'significant growth' : 'new opportunities'} hongi. Planets ${i % 3 === 0 ? 'favorable conditions' : 'positive changes'} laayenge.`
      },
      highlight: i === 0 || i % 3 === 0,
      icons: ['sparkle', 'star', 'sparkle'],
      aiGenerated: i === 0 && !!(realText || aiDomainContent)
    };
  });

  // Load audio from IndexedDB cache on mount and language change
  const loadCachedAudio = useCallback(async () => {
    try {
      const userId = localStorage.getItem('user_id') || localStorage.getItem('tts_user_id') || localStorage.getItem('session_id');
      if (!userId) return;
      
      const audioBase64 = await LocalAudioCache.getAudioLocal(userId, `domain_${domainTheme}`, selectedLanguage);
      if (audioBase64) {
        // Convert base64 to audio URL
        const audioBlob = new Blob(
          [Uint8Array.from(atob(audioBase64), c => c.charCodeAt(0))],
          { type: 'audio/mp3' }
        );
        const audioUrl = URL.createObjectURL(audioBlob);
        
        // Create or update audio element
        if (audioRef.current) {
          audioRef.current.pause();
          URL.revokeObjectURL(audioRef.current.src);
        }
        const audio = new Audio(audioUrl);
        audio.onended = () => setIsAudioPlaying(false);
        audio.onerror = () => {
          console.warn('Audio playback error');
          setIsAudioPlaying(false);
        };
        audioRef.current = audio;
        setAudioLoaded(true);
        console.log(`Audio loaded from cache: domain_${domainTheme}/${selectedLanguage}`);
      } else {
        setAudioLoaded(false);
        console.log(`No cached audio for domain_${domainTheme}/${selectedLanguage}`);
      }
    } catch (err) {
      console.error('Error loading cached audio:', err);
      setAudioLoaded(false);
    }
  }, [domainTheme, selectedLanguage]);

  // Load audio on mount and when language changes
  useEffect(() => {
    loadCachedAudio();
    return () => {
      // Cleanup audio on unmount
      if (audioRef.current) {
        audioRef.current.pause();
        if (audioRef.current.src) URL.revokeObjectURL(audioRef.current.src);
        audioRef.current = null;
      }
    };
  }, [loadCachedAudio]);

  const toggleAudio = () => {
    if (!audioRef.current || !audioLoaded) {
      toast.info('Audio not available for this domain yet');
      return;
    }
    if (isAudioPlaying) {
      audioRef.current.pause();
      setIsAudioPlaying(false);
    } else {
      audioRef.current.play().then(() => {
        setIsAudioPlaying(true);
      }).catch(err => {
        console.error('Audio play error:', err);
        toast.error('Could not play audio. Try tapping again.');
      });
    }
  };

  // Loading screen effect - INSTANT if data exists
  useEffect(() => {
    if (hasUnifiedData) {
      setIsLoading(false);
      setShowSkipButton(true);
      setAudioReady(true);
      return;
    }
    
    let currentProgress = 0;
    const interval = setInterval(() => {
      currentProgress += 2;
      setProgress(currentProgress);
      if (currentProgress >= 100) {
        clearInterval(interval);
        setTimeout(() => {
          setIsLoading(false);
          setShowSkipButton(true);
          setAudioReady(true);
        }, 300);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [hasUnifiedData]);

  const handleSageClick = () => {
    if (isNarrating) return;
    
    if (!audioReady) {
      toast.info('Please wait, preparing your story...', { duration: 2000 });
      return;
    }

    setIsNarrating(true);
    setShowNarration(true);
    setSelectedYear(0);
    
    // Start audio playback if available
    if (audioRef.current && audioLoaded) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().then(() => {
        setIsAudioPlaying(true);
      }).catch(err => console.warn('Auto-play blocked:', err));
    }
    
    progressThroughYears(0);
  };

  const progressThroughYears = async (startYear) => {
    if (startYear >= futurePredictions.length) {
      setIsNarrating(false);
      setShowNarration(false);
      setStoryCompleted(true);
      setShowSkipButton(false);
      toast.success('🎉 Your future story is complete!', { duration: 4000 });
      return;
    }

    setSelectedYear(startYear);
    await new Promise(resolve => setTimeout(resolve, 5000)); // 5 seconds per year
    progressThroughYears(startYear + 1);
  };

  const handleSkipStory = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsAudioPlaying(false);
    }
    
    setIsNarrating(false);
    setShowNarration(false);
    setStoryCompleted(true);
    setShowSkipButton(false);
    toast.info('Story skipped!');
  };

  const currentPrediction = futurePredictions[selectedYear] || futurePredictions[0];
  const narrationText = currentPrediction?.narration?.[selectedLanguage] || '';

  // Golden theme color
  const goldenColor = '#edc161';

  return (
    <>
      {/* Loading Screen */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'radial-gradient(ellipse at center, rgba(237, 193, 97, 0.08) 0%, #020712 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10001,
              padding: '20px'
            }}
          >
            {/* Domain Icon */}
            <motion.div
              animate={{ rotate: 360, scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              style={{
                fontSize: '80px',
                marginBottom: '30px'
              }}
            >
              {domain.icon}
            </motion.div>

            {/* Domain Name */}
            <h2 style={{
              fontSize: 'clamp(24px, 6vw, 32px)',
              fontWeight: 700,
              color: goldenColor,
              marginBottom: '12px',
              textAlign: 'center'
            }}>
              {domain.name}
            </h2>

            <p style={{
              fontSize: 'clamp(14px, 3.5vw, 16px)',
              color: 'rgba(237, 193, 97, 0.7)',
              marginBottom: '40px',
              textAlign: 'center'
            }}>
              Preparing your future journey...
            </p>

            {/* Progress Bar */}
            <div style={{
              width: '80%',
              maxWidth: '400px',
              height: '4px',
              background: 'rgba(237, 193, 97, 0.2)',
              borderRadius: '2px',
              overflow: 'hidden'
            }}>
              <motion.div
                initial={{ width: '0%' }}
                animate={{ width: `${progress}%` }}
                style={{
                  height: '100%',
                  background: `linear-gradient(90deg, ${goldenColor}, ${goldenColor}dd)`,
                  borderRadius: '2px'
                }}
              />
            </div>

            <p style={{
              marginTop: '16px',
              fontSize: '14px',
              color: 'rgba(237, 193, 97, 0.6)'
            }}>
              {progress}%
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Story Screen - Golden Theme */}
      <div style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at center, rgba(237, 193, 97, 0.08) 0%, #020712 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: '20px'
      }}>
        {/* Stars Background */}
        <div style={{
          position: 'fixed',
          inset: 0,
          background: `
            radial-gradient(2px 2px at 20% 30%, white, transparent),
            radial-gradient(1px 1px at 50% 50%, white, transparent),
            radial-gradient(1px 1px at 80% 70%, white, transparent)
          `,
          backgroundSize: '100vw 100vh',
          opacity: 0.4,
          pointerEvents: 'none'
        }} />

        {/* Small Dashboard Button */}
        <motion.button
          onClick={() => navigate('/dashboard')}
          whileTap={{ scale: 0.95 }}
          data-testid="dashboard-btn"
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            background: 'rgba(237, 193, 97, 0.15)',
            border: '1px solid rgba(237, 193, 97, 0.3)',
            borderRadius: '8px',
            padding: '6px 10px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            zIndex: 100
          }}
        >
          <Home size={14} style={{ color: goldenColor }} />
          <span style={{ color: goldenColor, fontSize: '11px', fontWeight: 600 }}>Dashboard</span>
        </motion.button>

        {/* Audio Play/Pause Button */}
        {audioLoaded && (
          <motion.button
            onClick={toggleAudio}
            whileTap={{ scale: 0.95 }}
            data-testid="audio-toggle-btn"
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: isAudioPlaying ? 'rgba(237, 193, 97, 0.3)' : 'rgba(237, 193, 97, 0.15)',
              border: `1px solid rgba(237, 193, 97, ${isAudioPlaying ? '0.6' : '0.3'})`,
              borderRadius: '8px',
              padding: '6px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              zIndex: 100
            }}
          >
            {isAudioPlaying ? <Pause size={14} style={{ color: goldenColor }} /> : <Volume2 size={14} style={{ color: goldenColor }} />}
            <span style={{ color: goldenColor, fontSize: '11px', fontWeight: 600 }}>
              {isAudioPlaying ? 'Pause' : 'Listen'}
            </span>
          </motion.button>
        )}

        {/* Domain Header */}
        <div style={{
          position: 'absolute',
          top: '100px',
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
          zIndex: 90,
          width: '90%',
          maxWidth: '600px'
        }}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{
              fontSize: 'clamp(24px, 5vw, 32px)',
              fontWeight: 700,
              color: goldenColor,
              marginBottom: '8px',
              textShadow: `0 0 30px ${goldenColor}60`
            }}
          >
            {domain.name}
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            style={{
              fontSize: 'clamp(13px, 2.8vw, 16px)',
              color: `${goldenColor}90`,
              fontWeight: 500,
              letterSpacing: '2px'
            }}
          >
            FUTURE PREDICTIONS
          </motion.div>
        </div>

        {/* Language Selector */}
        <div style={{
          position: 'absolute',
          top: '56px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '6px',
          zIndex: 100,
          background: 'rgba(10, 22, 40, 0.6)',
          backdropFilter: 'blur(15px)',
          padding: '6px 10px',
          borderRadius: '24px',
          border: `1.5px solid ${goldenColor}40`
        }}>
          {[
            { key: 'hindi', label: 'हिंदी' },
            { key: 'hinglish', label: 'Hinglish' },
            { key: 'english', label: 'English' }
          ].map((lang) => (
            <motion.button
              key={lang.key}
              onClick={() => {
                // Stop current audio when switching language
                if (audioRef.current && isAudioPlaying) {
                  audioRef.current.pause();
                  setIsAudioPlaying(false);
                }
                setSelectedLanguage(lang.key);
              }}
              whileTap={{ scale: 0.95 }}
              style={{
                padding: '6px 14px',
                borderRadius: '16px',
                border: 'none',
                background: selectedLanguage === lang.key 
                  ? `${goldenColor}30`
                  : 'transparent',
                color: selectedLanguage === lang.key ? goldenColor : '#9CA3AF',
                fontSize: 'clamp(11px, 2.5vw, 13px)',
                fontWeight: selectedLanguage === lang.key ? 700 : 500,
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              {lang.label}
            </motion.button>
          ))}
        </div>

        {/* Timeline */}
        <div style={{
          position: 'absolute',
          bottom: '200px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '90%',
          maxWidth: '600px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'relative'
          }}>
            {/* Timeline Line */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              height: '2px',
              background: `${goldenColor}40`,
              transform: 'translateY(-50%)'
            }} />

            {/* Progress Line */}
            <motion.div
              animate={{
                width: isNarrating ? `${((selectedYear + 1) / futurePredictions.length) * 100}%` : '0%'
              }}
              style={{
                position: 'absolute',
                top: '50%',
                left: 0,
                height: '3px',
                background: goldenColor,
                transform: 'translateY(-50%)',
                boxShadow: `0 0 10px ${goldenColor}`,
                borderRadius: '2px'
              }}
            />

            {/* Year Dots */}
            {futurePredictions.map((pred, idx) => (
              <motion.div
                key={idx}
                animate={{
                  scale: selectedYear === idx && isNarrating ? 2 : 1,
                  y: selectedYear === idx && isNarrating ? -20 : 0
                }}
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: (selectedYear === idx && isNarrating) || (selectedYear > idx && isNarrating) 
                    ? goldenColor 
                    : `${goldenColor}50`,
                  border: `2px solid ${goldenColor}`,
                  boxShadow: selectedYear === idx && isNarrating ? `0 0 20px ${goldenColor}` : 'none',
                  position: 'relative',
                  zIndex: selectedYear === idx ? 10 : 1,
                  cursor: 'pointer'
                }}
              >
                {selectedYear === idx && isNarrating && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{
                      position: 'absolute',
                      top: '-40px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: '18px',
                      fontWeight: 700,
                      color: goldenColor,
                      whiteSpace: 'nowrap',
                      textShadow: `0 0 15px ${goldenColor}`
                    }}
                  >
                    {pred.year}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Sage Avatar */}
        <div style={{
          position: 'absolute',
          bottom: '280px',
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
          cursor: !isNarrating ? 'pointer' : 'default'
        }}
          onClick={handleSageClick}
        >
          <motion.div
            animate={{ 
              y: [0, -10, 0],
              filter: isNarrating 
                ? `drop-shadow(0 0 35px ${goldenColor})`
                : `drop-shadow(0 0 20px ${goldenColor}80)`
            }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            <img
              src="/sage-final.png"
              alt="Sage"
              style={{
                width: 'clamp(100px, 25vw, 140px)',
                height: 'auto',
                opacity: 0.95
              }}
              onError={(e) => {
                e.target.src = '/sage.png';
              }}
            />
          </motion.div>

          {!isNarrating && (
            <motion.div
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
              style={{
                fontSize: 'clamp(13px, 2.8vw, 16px)',
                color: goldenColor,
                marginTop: '12px',
                fontWeight: 700,
                textShadow: `0 0 12px ${goldenColor}60`
              }}
            >
              ✨ Tap to Start Your Future Story ✨
            </motion.div>
          )}
        </div>

        {/* Narration Text Bubble */}
        <AnimatePresence>
          {showNarration && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20 }}
              style={{
                position: 'absolute',
                bottom: '100px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '90%',
                maxWidth: '600px'
              }}
            >
              {/* Speech bubble pointer */}
              <div style={{
                width: 0,
                height: 0,
                margin: '0 auto',
                borderLeft: '15px solid transparent',
                borderRight: '15px solid transparent',
                borderBottom: `15px solid ${goldenColor}30`
              }} />
              
              <div style={{
                background: `linear-gradient(135deg, ${goldenColor}20, ${goldenColor}08)`,
                backdropFilter: 'blur(20px)',
                padding: '20px',
                borderRadius: '20px',
                border: `2px solid ${goldenColor}50`,
                textAlign: 'center',
                boxShadow: `0 8px 30px ${goldenColor}20`
              }}>
                <p style={{
                  fontSize: 'clamp(14px, 3.5vw, 16px)',
                  color: '#E8E8E8',
                  lineHeight: 1.7,
                  margin: 0
                }}>
                  {narrationText}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Completion Overlay with Dashboard Button */}
        <AnimatePresence>
          {storyCompleted && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(2, 7, 18, 0.95)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                padding: '20px'
              }}
            >
              <motion.div
                initial={{ scale: 0.8, y: 30 }}
                animate={{ scale: 1, y: 0 }}
                style={{
                  maxWidth: '400px',
                  width: '100%',
                  textAlign: 'center'
                }}
              >
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  style={{ fontSize: '80px', marginBottom: '20px' }}
                >
                  {domain.icon}
                </motion.div>

                <h2 style={{
                  fontSize: 'clamp(24px, 6vw, 32px)',
                  fontWeight: 700,
                  color: goldenColor,
                  marginBottom: '12px',
                  textShadow: `0 0 20px ${goldenColor}60`
                }}>
                  Journey Complete!
                </h2>

                <p style={{
                  fontSize: 'clamp(14px, 3.5vw, 16px)',
                  color: `${goldenColor}90`,
                  marginBottom: '40px'
                }}>
                  Your {domain.name.toLowerCase()} future has been revealed.
                </p>

                {/* Dashboard Button */}
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => navigate('/dashboard')}
                  data-testid="back-to-dashboard-btn"
                  style={{
                    width: '100%',
                    padding: '18px',
                    fontSize: '17px',
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${goldenColor} 0%, #d4a84b 100%)`,
                    border: 'none',
                    borderRadius: '20px',
                    color: '#020712',
                    cursor: 'pointer',
                    boxShadow: `0 8px 30px ${goldenColor}50`
                  }}
                >
                  Back to Dashboard
                </motion.button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default DomainStoryFuture;
